import { useState, useMemo, useCallback, useEffect } from 'react';

/**
 * Handles a year/month/day group, all values are 1 based
 *
 * @param {Object} params
 * @param {number} params.day - initial day
 * @param {number} params.month - initial month
 * @param {number} params.year - initial year
 * @returns {DayMonthYearState}
 */
export default function useDayMonthYear({ day: _day, month: _month, year: _year } = {}) {
  const [year, _setYear] = useState(validateYear(_year) ? _year : null);
  const [month, _setMonth] = useState(validateMonth(_month) ? _month : null);
  const [day, _setDay] = useState(validateDay(_day, getDaysInMonth(_year, _month)) ? _day : null);

  const daysInMonth = useMemo(() => getDaysInMonth(year, month), [month, year]);

  const setDay = useCallback(
    (d) => {
      if (validateDay(d, daysInMonth)) {
        _setDay(d);
      }
    },
    [daysInMonth]
  );
  const setMonth = useCallback((m) => {
    if (validateMonth(m)) {
      _setMonth(m);
    }
  }, []);
  const setYear = useCallback((y) => {
    if (validateYear(y)) {
      _setYear(y);
    }
  }, []);

  const reset = useCallback(() => {
    _setYear(null);
    _setMonth(null);
    _setDay(null);
  }, []);

  // unset the day if daysInMonth changed and day is not in range
  useEffect(() => {
    if (!validateDay(day, daysInMonth)) {
      _setDay(null);
    }
  }, [day, daysInMonth]);

  return {
    day,
    month,
    year,
    daysInMonth,
    setDay,
    setMonth,
    setYear,
    reset,
  };
}

/**
 * Get the days in a month, or 28 if no month is supplied
 * @param {number} year
 * @param {number} month
 */
function getDaysInMonth(year, month) {
  // 0 is the last day of prev month (month is 1 based)
  return month ? new Date(year, month, 0).getDate() : 28;
}

function validateDay(day, daysInMonth) {
  return !isNaN(day) && day >= 1 && day <= daysInMonth;
}
function validateMonth(month) {
  return !isNaN(month) && month >= 1 && month <= 12;
}
function validateYear(year) {
  return !isNaN(year);
}

/**
 * @typedef {{
    day: number,
    month: number,
    year: number,
    daysInMonth: number,
    setDay: (day: number) => void,
    setMonth: (month: number) => void,,
    setYear: (year: number) => void,,
    reset: () => void,
  }} DayMonthYearState
 */
